import { useState } from 'react';
import { 
  shouldUseChunkedUpload,
  optimizeVideoForUpload,
  supportsClientSideProcessing,
  uploadCaptionVideoWithProgress,
  initializeChunkedUpload,
  uploadVideoChunk,
  calculateChunkCount,
  getFileChunk,
  finalizeChunkedUpload
} from '../../../services/videoCaptionApi';

interface UploadProgress {
  stage: 'chunking' | 'uploading' | 'assembling';
  currentChunk?: number;
  totalChunks?: number;
  percentage: number;
  message: string;
}

interface UploadStats {
  startTime: number;
  bytesUploaded: number;
  speed: number;
  eta: number;
}

export const useUploadLogic = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isChunkedUpload, setIsChunkedUpload] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({
    stage: 'chunking',
    percentage: 0,
    message: 'Preparing upload...'
  });
  const [uploadStats, setUploadStats] = useState<UploadStats>({
    startTime: 0,
    bytesUploaded: 0,
    speed: 0,
    eta: 0
  });
  const [retryAttempts, setRetryAttempts] = useState<Map<number, number>>(new Map());

  const [fileWarning, setFileWarning] = useState<{
    show: boolean;
    size: string;
    estimatedTime: string;
    chunks: number;
  } | null>(null);

  // Start upload with automatic optimization
  const startUpload = async (file: File) => {
    setIsLoading(true);
    setError(null);
    setFileWarning(null);
    
    console.log('🚀 Starting upload process...');
    console.log(`📁 File: ${file.name}`);
    console.log(`📏 Size: ${(file.size / (1024 * 1024)).toFixed(2)} MB`);
    console.log(`🧪 Browser support check:`, supportsClientSideProcessing());
    
    try {
      let fileToUpload = file;
      const fileSize = file.size;
      const fileSizeMB = fileSize / (1024 * 1024);
      
      // Debug the optimization check
      const shouldOptimize = fileSizeMB >= 100 && fileSizeMB < 1024;
      const browserSupports = supportsClientSideProcessing();
      
      console.log(`🔍 Optimization check:`);
      console.log(`  - File size check (100MB-1GB): ${shouldOptimize} (${fileSizeMB.toFixed(2)}MB)`);
      console.log(`  - Browser support: ${browserSupports}`);
      console.log(`  - Will optimize: ${shouldOptimize && browserSupports}`);
      
      // 🔧 TEMPORARY FIX: Disable optimization to preserve audio
      // The current optimization uses MediaRecorder which strips audio
      const skipOptimization = true;
      
      // Automatically optimize files between 100MB and 1GB
      if (shouldOptimize && browserSupports && !skipOptimization) {
        console.log(`🔧 Starting automatic optimization for ${fileSizeMB.toFixed(2)}MB file...`);
        
        try {
          const { optimizedFile, compressionRatio } = await optimizeVideoForUpload(file, {
            maxWidth: 1280,
            maxHeight: 720,
            quality: 0.6
          });
          
          const originalSizeMB = file.size / (1024 * 1024);
          const optimizedSizeMB = optimizedFile.size / (1024 * 1024);
          
          console.log(`✅ Optimization complete!`);
          console.log(`📊 Original size: ${originalSizeMB.toFixed(2)} MB`);
          console.log(`📊 Optimized size: ${optimizedSizeMB.toFixed(2)} MB`);
          console.log(`📊 Compression ratio: ${compressionRatio.toFixed(2)}x`);
          
          fileToUpload = optimizedFile;
        } catch (optimizationError) {
          console.warn('⚠️ Optimization failed, using original file:', optimizationError);
        }
      } else {
        console.log('⏭️ Skipping optimization - conditions not met');
      }
      
      console.log('📤 Starting file upload...');
      
      // Check if we should use chunked upload
      const useChunkedUpload = shouldUseChunkedUpload(fileToUpload.size);
      console.log(`🔄 Upload method: ${useChunkedUpload ? 'CHUNKED' : 'DIRECT'} (${fileSizeMB.toFixed(2)}MB)`);
      
      if (useChunkedUpload) {
        setIsChunkedUpload(true);
        return await performChunkedUpload(fileToUpload);
      } else {
        // Use direct upload for smaller files
        const result = await uploadCaptionVideoWithProgress(fileToUpload);
        console.log('✅ Direct upload completed successfully:', result);
        return result;
      }
      
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed');
      throw uploadError;
    } finally {
      setIsLoading(false);
    }
  };

  // Chunked upload implementation
  const performChunkedUpload = async (file: File) => {
    console.log('🧩 Starting chunked upload process...');
    
    const totalChunks = calculateChunkCount(file.size);
    const chunkSize = 50 * 1024 * 1024; // 50MB chunks
    
    console.log(`📊 Chunked upload details:`);
    console.log(`  - Total chunks: ${totalChunks}`);
    console.log(`  - Chunk size: ${(chunkSize / (1024 * 1024)).toFixed(2)}MB`);
    
    try {
      // Step 1: Initialize chunked upload session
      console.log('🔄 Initializing chunked upload session...');
      setUploadProgress({
        stage: 'chunking',
        percentage: 0,
        message: 'Initializing chunked upload session...'
      });
      
      const session = await initializeChunkedUpload(
        file.name,
        file.size,
        file.type,
        totalChunks,
        chunkSize
      );
      
      console.log(`✅ Session initialized: ${session.sessionId}`);
      
      // Step 2: Upload chunks
      console.log('📤 Starting chunk uploads...');
      setUploadProgress({
        stage: 'uploading',
        currentChunk: 0,
        totalChunks,
        percentage: 0,
        message: 'Uploading chunks...'
      });
      
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        console.log(`📦 Uploading chunk ${chunkIndex + 1}/${totalChunks}...`);
        
        const chunkBlob = getFileChunk(file, chunkIndex, chunkSize);
        
        await uploadVideoChunk(session.sessionId, chunkIndex, chunkBlob);
        
        const progress = Math.round(((chunkIndex + 1) / totalChunks) * 100);
        console.log(`✅ Chunk ${chunkIndex + 1} uploaded successfully (${progress}%)`);
        
        setUploadProgress({
          stage: 'uploading',
          currentChunk: chunkIndex + 1,
          totalChunks,
          percentage: progress,
          message: `Uploaded chunk ${chunkIndex + 1} of ${totalChunks}`
        });
      }
      
      // Step 3: Finalize upload and trigger assembly
      console.log('🔄 Finalizing chunked upload and triggering assembly...');
      setUploadProgress({
        stage: 'assembling',
        percentage: 100,
        message: 'All chunks uploaded - finalizing and assembling file...'
      });
      
      const finalizeResult = await finalizeChunkedUpload(session.sessionId);
      console.log('✅ Chunked upload finalized successfully:', finalizeResult);
      
      setUploadProgress({
        stage: 'assembling',
        percentage: 100,
        message: 'File assembly completed - video processing started'
      });
      
      console.log('🎉 Complete chunked upload process finished!');
      
      return {
        jobId: finalizeResult.jobId || session.sessionId,
        projectId: finalizeResult.projectId || session.sessionId,
        message: finalizeResult.message || 'Chunked upload completed successfully'
      };
      
    } catch (error) {
      console.error('💥 Chunked upload failed:', error);
      setError(error instanceof Error ? error.message : 'Chunked upload failed');
      throw error;
    }
  };

  return {
    // States
    isLoading,
    error,
    isChunkedUpload,
    uploadProgress,
    uploadStats,
    retryAttempts,
    fileWarning,
    
    // Actions
    startUpload,
    setError,
    setFileWarning
  };
};